import { format, parse, isSameDay, differenceInDays, differenceInCalendarMonths, subDays } from 'date-fns';

/**
 * Normaliza a data da tarefa para yyyy-MM-dd.
 */
export function taskDateToYYYYMMDD(task) {
  const v = task?.date;
  if (v == null || (typeof v === 'string' && v.trim() === '')) return null;
  if (typeof v === 'string') {
    const s = v.trim();
    if (s.length >= 10 && s[4] === '-' && s[7] === '-') return s.slice(0, 10);
    const parts = s.split(/[/-]/).map(p => (p || '').trim());
    if (parts.length === 3) {
      const [a, b, c] = parts;
      if (a.length === 4) return `${a}-${String(b).padStart(2, '0')}-${String(c).padStart(2, '0')}`;
      if (c.length === 4) return `${c}-${String(b).padStart(2, '0')}-${String(a).padStart(2, '0')}`;
    }
  }
  if (typeof v?.toDate === 'function') return format(v.toDate(), 'yyyy-MM-dd');
  try {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : format(d, 'yyyy-MM-dd');
  } catch {
    return null;
  }
}

/**
 * Verifica se a tarefa (com recorrência) ocorre numa data.
 * Semana = 7 dias; mês = mesmo dia do mês (28/29/30/31).
 */
export function isTaskOccurringOnDate(task, dateStr) {
  const taskDateStr = taskDateToYYYYMMDD(task);
  if (!taskDateStr) return false;
  let taskDate;
  let checkDate;
  try {
    taskDate = parse(taskDateStr, 'yyyy-MM-dd', new Date());
    checkDate = parse(dateStr, 'yyyy-MM-dd', new Date());
  } catch {
    return false;
  }
  if (checkDate < taskDate) return false;

  const hasRecurrence = task.autoSchedule && task.repeatType;
  if (!hasRecurrence) return isSameDay(taskDate, checkDate);

  const repeatType = task.repeatType || 'DAILY';
  const intervalDays = Math.max(1, Number(task.repeatIntervalDays) || 1);

  switch (repeatType) {
    case 'DAILY':
      return true;
    case 'EVERY_OTHER_DAY': {
      const days = differenceInDays(checkDate, taskDate);
      return days % 2 === 0;
    }
    case 'WEEKLY': {
      const days = differenceInDays(checkDate, taskDate);
      return days % 7 === 0;
    }
    case 'MONTHLY': {
      if (taskDate.getDate() !== checkDate.getDate()) return false;
      return differenceInCalendarMonths(checkDate, taskDate) >= 0;
    }
    case 'CUSTOM_DAYS': {
      const days = differenceInDays(checkDate, taskDate);
      return days % intervalDays === 0;
    }
    default:
      return isSameDay(taskDate, checkDate);
  }
}

/**
 * Verifica se a ocorrência da tarefa numa data está concluída.
 * Tarefas recorrentes: usa completedOccurrences[] (datas "yyyy-MM-dd" concluídas).
 * Tarefas não recorrentes: usa isCompleted e completedDate.
 */
export function isOccurrenceCompleted(task, dateStr) {
  const dateKey = typeof dateStr === 'string' ? dateStr.slice(0, 10) : null;
  if (!dateKey) return false;

  const isRecurring = task.autoSchedule && task.repeatType;
  if (isRecurring) {
    const list = Array.isArray(task.completedOccurrences) ? task.completedOccurrences : [];
    return list.includes(dateKey);
  }

  return !!(task.isCompleted || task.status === 'Concluído') && (task.completedDate && String(task.completedDate).slice(0, 10) === dateKey);
}

/**
 * Verifica se a tarefa deve aparecer numa data (ocorrência nesse dia OU arrasto do dia anterior não concluído).
 * "Ficar para fazer do dia de hoje para amanhã" = aparece no dia seguinte.
 */
export function shouldShowTaskOnDate(task, dateStr) {
  if (isTaskOccurringOnDate(task, dateStr)) return true;
  try {
    const date = parse(dateStr, 'yyyy-MM-dd', new Date());
    const yesterday = subDays(date, 1);
    const yesterdayStr = format(yesterday, 'yyyy-MM-dd');
    if (isTaskOccurringOnDate(task, yesterdayStr) && !isOccurrenceCompleted(task, yesterdayStr)) return true;
  } catch {
    // ignore
  }
  return false;
}
